/**
 * One person in a short sample list — "Ritika Shinde · 28 · Pune · 9 orders",
 * with a masked contact and a source tag — and the initials avatar beside them.
 * The concept's `.sb-person`.
 *
 * ## THE CONTACT IS MASKED BY THE CALLER, AND THIS FILE NEVER FORMATS ONE
 *
 * This database holds real phone numbers and addresses. A component that took
 * a raw phone and masked it itself would be one refactor away from a caller
 * passing a pre-formatted, unmasked string and this file printing it as-is —
 * the mask would look like it lived here and nobody would add one upstream.
 * So `contact` is an opaque string drawn in mono, and the header says
 * plainly: whoever passes it has already masked it. The screens that list
 * people today mask on the server; keep it that way.
 *
 * ## THE AVATAR'S COLOUR IS DETERMINISTIC AND FROM THE THEME
 *
 * The concept drew each avatar as an `hsl()` gradient hashed from the id —
 * 360 untokenised colours, some of them status-green and alarm-pink, and white
 * initials that dropped under 3:1 on the yellow ones. Here a hash of `seed`
 * picks one of five theme pairs (the four identity slots and `brand-ink`),
 * each drawn as the colour on its own 24% wash. At rest that measures 5.09:1
 * at worst (lilac, over surface-1). Under the row's hover tint it drops: lilac
 * initials measure 4.29:1 over surface-2 and 4.47:1 over a card (slice 66
 * review; the first header claimed 5.09:1 everywhere). That is left as it is
 * on purpose — the avatar is `aria-hidden` and repeats initials of the name
 * printed beside it, so it is decoration under WCAG 1.4.3 rather than text a
 * reader depends on — and it is written down here rather than hidden. The
 * same seed always gets the same colour, so a person does not change colour
 * between two lists; "R S" read aloud before "Ritika Shinde" would be noise.
 *
 * The hover is `bg-white/[0.04]` — the concept's 0.035 moved onto the tint
 * ladder this slice fixed for new code.
 *
 * ## THE HOLE IT LEAVES
 *
 * `href` renders a plain `<a>`. In a router app that is a full page load; a
 * caller inside react-router passes a `<Link>` as `name` instead and leaves
 * `href` unset. The package has no router dependency and should not grow one
 * for a list row.
 */
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { Tag } from './Tag';

/** Colour on its own 24% wash: five pairs, every one ≥ 5.09:1 at rest (see the header for hover). */
const AVATAR_TONES = [
  'bg-ident-a/24 text-ident-a',
  'bg-ident-b/24 text-ident-b',
  'bg-ident-c/24 text-ident-c',
  'bg-ident-d/24 text-ident-d',
  'bg-brand-ink/24 text-brand-ink',
] as const;

/** A small stable string hash (31-multiplier, like Java's) — not for security. */
function hashSeed(seed: string): number {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return hash;
}

function initialsOf(name: string): string {
  const letters = name
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .filter((char) => char !== '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return letters === '' ? '·' : letters;
}

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** The person's name; its first two initials are drawn. */
  name: string;
  /** Anything stable for this person — an id. Picks the colour. */
  seed: string;
}

export function Avatar({ name, seed, className, ...rest }: AvatarProps) {
  const tone = AVATAR_TONES[hashSeed(seed) % AVATAR_TONES.length];
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid size-[30px] shrink-0 place-items-center rounded-full text-[0.68rem] font-semibold tracking-[0.02em]',
        tone,
        className,
      )}
      {...rest}
    >
      {initialsOf(name)}
    </span>
  );
}

export interface PersonRowProps extends Omit<HTMLAttributes<HTMLLIElement>, 'children'> {
  name: string;
  /** Seeds the avatar. Defaults to `name`; pass an id where two people can share one. */
  seed?: string;
  /** The muted line under the name — "28 · Pune · 9 orders". */
  meta?: ReactNode;
  /** Already masked by the caller. This component prints it as given. */
  contact?: string;
  /** Right-hand tag under the contact — usually a source `Tag`. */
  tag?: ReactNode;
  /** Draws a "new" tag beside the name. */
  fresh?: boolean;
  /** Makes the name a plain link. See the header for router apps. */
  href?: string;
}

export function PersonRow({ name, seed, meta, contact, tag, fresh = false, href, className, ...rest }: PersonRowProps) {
  return (
    <li
      className={cn(
        'flex min-w-0 items-center gap-2.5 rounded-sm px-1.5 py-[7px] transition-colors hover:bg-white/[0.04]',
        className,
      )}
      {...rest}
    >
      <Avatar name={name} seed={seed ?? name} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1.5 text-[0.84rem] text-text">
          {href !== undefined ? (
            <a href={href} className={cn('truncate rounded-xs hover:underline', focusRing)}>
              {name}
            </a>
          ) : (
            <span className="truncate">{name}</span>
          )}
          {fresh && <Tag tone="good">new</Tag>}
        </span>
        {meta !== undefined && meta !== null && (
          <span className="truncate text-[0.72rem] text-muted">{meta}</span>
        )}
      </span>
      {(contact !== undefined || (tag !== undefined && tag !== null)) && (
        <span className="flex shrink-0 flex-col items-end gap-0.5">
          {contact !== undefined && (
            <span className="whitespace-nowrap font-mono text-[0.7rem] text-muted">{contact}</span>
          )}
          {tag}
        </span>
      )}
    </li>
  );
}

export type PersonListProps = HTMLAttributes<HTMLUListElement>;

/** The `<ul>` a set of PersonRows sits in. */
export function PersonList({ className, ...rest }: PersonListProps) {
  return <ul className={cn('grid min-w-0 grid-cols-[minmax(0,1fr)] gap-0.5', className)} {...rest} />;
}
